import { c as _c } from "react/compiler-runtime";
import { useSortable } from '@dnd-kit/sortable';
import { Dropdown, IconButton, Tooltip, useTranslationContext } from 'platform/components';
import { match } from 'ts-pattern';
import { useRef, useState } from 'react';
import { COLUMN_SEPARATOR_STYLE, HEADER_CELL_BORDER_BOTTOM, PINNED_INDICATOR_STYLE, PINNED_LEFT_EDGE_LINE_STYLE, PINNED_RIGHT_EDGE_LINE_STYLE, RESIZE_INDICATOR_LEFT_STYLE, RESIZE_INDICATOR_STYLE } from '../../../../constants/borderStyles';
import { DEFAULT_COL_WIDTH } from '../../../../constants/defaultColWidth';
import { useColumnMenuContext } from '../../../../context/ColumnMenuContext';
import { useIsMobileView } from '../../../../hooks/useIsMobileView';
import { useColumnResizingAllowed, useColumnSortMode } from '../../../../store/selectors';
import { selectHeaderCellOverlayState } from '../../../../store/selectors/selectHeaderCellOverlayState';
import { useGridSettingsSafe } from '../../../../store/selectors/useGridSettingsSafe';
import { useEagGridSelector } from '../../../../store/useEagGridSelector';
import type { EagColumn } from '../../../../types/EagColumn';
import type { EagColumnState } from '../../../../types/EagColumnState';
import { getPinnedEdgeShadowClass } from '../../../../utils/getPinnedEdgeShadowClass';
import { getRowPaddingClass, LineHeightToPx } from '../../../../utils/lineHeightToValue';
import { ColumnHeaderMenu } from './components/ColumnHeaderMenu/ColumnHeaderMenu';
import { computeHeaderCellLayout } from './utils/computeHeaderCellLayout';
import { computeHeaderCellPinnedStyle } from './utils/computeHeaderCellPinnedStyle';
export type HeaderCellProps = {
  column: EagColumn;
  columnState: EagColumnState;
  colIndex: number;
  onSort: (key: string) => void;
  onResizeStart: (key: string, x: number, colIndex: number, resizeFromLeft?: boolean) => void;
  pinnedLeftOffset?: number;
  pinnedRightOffset?: number;
  // Pinning edge shadow — true on the boundary column of each pinned group
  isLastPinnedLeft?: boolean;
  isFirstPinnedRight?: boolean;
  /** Sticky top offset in px. 0 when no group header row is shown; GROUP_HEADER_ROW_HEIGHT when visible. */
  stickyTop: number;
};
export function HeaderCell(props) {
  const $ = _c(121);
  const overlay = useEagGridSelector(selectHeaderCellOverlayState);
  const gridSettings = useGridSettingsSafe();
  const heightPx = LineHeightToPx[gridSettings.headerHeight];
  const paddingClass = getRowPaddingClass[gridSettings.headerHeight];
  const columnResizingAllowed = useColumnResizingAllowed();
  const columnSortMode = useColumnSortMode();
  const columnMenu = useColumnMenuContext();
  const isMobile = useIsMobileView();
  const t = useTranslationContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const thRef = useRef(null);
  let t0;
  if ($[0] !== props.column.key) {
    t0 = {
      id: props.column.key,
      animateLayoutChanges: _temp
    };
    $[0] = props.column.key;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging
  } = useSortable(t0);
  const width = props.columnState.width ?? DEFAULT_COL_WIDTH;
  let t1;
  if ($[2] !== columnMenu.filterState || $[3] !== columnMenu.showColumnMenu || $[4] !== columnSortMode || $[5] !== gridSettings.headerHeight || $[6] !== isMobile || $[7] !== props.column || $[8] !== props.columnState || $[9] !== width) {
    t1 = computeHeaderCellLayout({
      column: props.column,
      columnState: props.columnState,
      width,
      headerHeight: gridSettings.headerHeight,
      showColumnMenu: columnMenu.showColumnMenu,
      columnSortMode,
      isMobile,
      filterState: columnMenu.filterState
    });
    $[2] = columnMenu.filterState;
    $[3] = columnMenu.showColumnMenu;
    $[4] = columnSortMode;
    $[5] = gridSettings.headerHeight;
    $[6] = isMobile;
    $[7] = props.column;
    $[8] = props.columnState;
    $[9] = width;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const cellLayout = t1;
  const useSmallButtons = cellLayout.useSmallButtons;
  const showMenuButton = cellLayout.showMenuButton;
  const showSortButton = cellLayout.showSortButton;
  const textPaddingRight = cellLayout.textPaddingRight;
  const isColumnFilterActive = cellLayout.isColumnFilterActive;
  const menuPriority = cellLayout.menuPriority;
  const columnHasMenuContent = cellLayout.columnHasMenuContent;
  const isPinned = props.columnState.pinned === "LEFT" || props.columnState.pinned === "RIGHT";
  const isEdge = (props.isLastPinnedLeft ?? false) || (props.isFirstPinnedRight ?? false);
  let t2;
  if ($[11] !== isEdge || $[12] !== props.columnState.pinned) {
    t2 = getPinnedEdgeShadowClass(props.columnState.pinned, isEdge);
    $[11] = isEdge;
    $[12] = props.columnState.pinned;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const shadowClass = t2;
  let t3;
  if ($[14] !== props.columnState.pinned || $[15] !== props.pinnedLeftOffset || $[16] !== props.pinnedRightOffset || $[17] !== props.stickyTop) {
    t3 = computeHeaderCellPinnedStyle({
      pinned: props.columnState.pinned,
      stickyTop: props.stickyTop,
      pinnedLeftOffset: props.pinnedLeftOffset,
      pinnedRightOffset: props.pinnedRightOffset
    });
    $[14] = props.columnState.pinned;
    $[15] = props.pinnedLeftOffset;
    $[16] = props.pinnedRightOffset;
    $[17] = props.stickyTop;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const pinnedStyle = t3;
  let t4;
  if ($[19] !== props.columnState.sort) {
    t4 = match(props.columnState.sort).with("ASC", _temp2).with("DESC", _temp3).otherwise(_temp4);
    $[19] = props.columnState.sort;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const sortIconValue = t4;
  let t5;
  if ($[21] !== props.columnState.sort) {
    t5 = match(props.columnState.sort).with("ASC", "DESC", _temp5).otherwise(_temp6);
    $[21] = props.columnState.sort;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const sortPriority = t5;
  const isSortActive = props.columnState.sort != null;
  const isActiveState = isSortActive || isColumnFilterActive;
  const desktopVisibilityClass = isActiveState ? "opacity-100" : "opacity-0 group-hover/header:opacity-100 transition-opacity duration-150";
  let t6;
  if ($[23] !== props.column.sortable || $[24] !== props.columnState.sort) {
    let t7;
    if ($[26] !== props.column.sortable) {
      t7 = () => props.column.sortable ? "none" as const : undefined;
      $[26] = props.column.sortable;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    t6 = match(props.columnState.sort).with("ASC", _temp7).with("DESC", _temp8).otherwise(t7);
    $[23] = props.column.sortable;
    $[24] = props.columnState.sort;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const ariaSortValue = t6;
  let t7;
  if ($[28] !== props) {
    t7 = event => {
      event.stopPropagation();
      props.onSort(props.column.key);
    };
    $[28] = props;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const handleSortClick = t7;
  const isRightPinned = props.columnState.pinned === "RIGHT";
  let t8;
  if ($[30] !== isRightPinned || $[31] !== props) {
    t8 = event_0 => {
      event_0.stopPropagation();
      props.onResizeStart(props.column.key, event_0.clientX, props.colIndex, isRightPinned);
    };
    $[30] = isRightPinned;
    $[31] = props;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleResizePointerDown = t8;
  const handleMenuButtonClick = _temp9;
  const isBeingResized = overlay.isResizing && overlay.resizingColumn === props.column.key;
  let t9;
  if ($[33] !== isDragging || $[34] !== isPinned || $[35] !== overlay.activeDragId || $[36] !== overlay.overColumnId || $[37] !== props.column.key) {
    t9 = match({
      isDropTarget: overlay.overColumnId === props.column.key && !(isDragging || overlay.activeDragId === props.column.key),
      isPinned
    }).with({
      isDropTarget: true
    }, _temp0).with({
      isPinned: true
    }, _temp1).otherwise(_temp10);
    $[33] = isDragging;
    $[34] = isPinned;
    $[35] = overlay.activeDragId;
    $[36] = overlay.overColumnId;
    $[37] = props.column.key;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const bgClass = t9;
  let t10;
  if ($[39] !== heightPx || $[40] !== pinnedStyle || $[41] !== width) {
    t10 = {
      width,
      maxWidth: width,
      height: heightPx,
      ...pinnedStyle,
      ...HEADER_CELL_BORDER_BOTTOM
    };
    $[39] = heightPx;
    $[40] = pinnedStyle;
    $[41] = width;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const thStyle = t10;
  const isDragSource = isDragging || overlay.activeDragId === props.column.key;
  const isDropTarget = overlay.overColumnId === props.column.key && !isDragSource;
  const isDropLeft = isDropTarget && overlay.dropSide === "left";
  const isDropRight = isDropTarget && overlay.dropSide === "right";
  const t11 = isDragSource ? 0.3 : 1;
  let t12;
  if ($[43] !== t11) {
    t12 = {
      opacity: t11
    };
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const dragStyle = t12;
  let t13;
  if ($[45] !== setNodeRef) {
    t13 = node => {
      setNodeRef(node);
      thRef.current = node;
    };
    $[45] = setNodeRef;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const t14 = `group/header relative text-xs font-semibold text-gray-600 text-ellipsis whitespace-nowrap p-0 select-none ${bgClass} ${shadowClass}`;
  let t15;
  if ($[47] !== dragStyle || $[48] !== thStyle) {
    t15 = {
      ...thStyle,
      ...dragStyle
    };
    $[47] = dragStyle;
    $[48] = thStyle;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const t16 = `flex items-center h-full min-w-0 overflow-hidden ${paddingClass}`;
  let t17;
  if ($[50] !== textPaddingRight) {
    t17 = {
      paddingRight: textPaddingRight
    };
    $[50] = textPaddingRight;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== props.column.headerName) {
    t18 = <span className="truncate block">{props.column.headerName}</span>;
    $[52] = props.column.headerName;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== props.column.headerName || $[55] !== t18) {
    t19 = <span className="min-w-0 flex-1 overflow-hidden"><Tooltip label={props.column.headerName}>{t18}</Tooltip></span>;
    $[54] = props.column.headerName;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== listeners || $[58] !== t16 || $[59] !== t17 || $[60] !== t19) {
    t20 = <div className={t16} style={t17} {...listeners}>{t19}</div>;
    $[57] = listeners;
    $[58] = t16;
    $[59] = t17;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const t21 = `absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 pr-1 ${bgClass} ${!isMobile ? desktopVisibilityClass : ""}`;
  let t22;
  if ($[62] !== columnHasMenuContent || $[63] !== columnMenu.showColumnMenu || $[64] !== handleSortClick || $[65] !== isMenuOpen || $[66] !== isMobile || $[67] !== menuPriority || $[68] !== props.column || $[69] !== props.columnState.pinned || $[70] !== showMenuButton || $[71] !== showSortButton || $[72] !== sortIconValue || $[73] !== sortPriority || $[74] !== t || $[75] !== useSmallButtons) {
    t22 = isMobile ? <>{columnMenu.showColumnMenu && columnHasMenuContent && <Dropdown isOpen={isMenuOpen} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)} placement="bottom-end" onButtonClick={handleMenuButtonClick} isFocusManagementDisabled={true} isKeyboardNavigationDisabled={true} dropdownControl={<IconButton icon="navigation/menu" size={useSmallButtons ? "small" : "default"} aria-label={t("page.dataGrid.column.optionsAriaLabel", {
        columnName: props.column.headerName
      })} />}><ColumnHeaderMenu column={props.column} columnPinned={props.columnState.pinned} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} /></Dropdown>}</> : <>{showSortButton && <IconButton icon={sortIconValue} size={useSmallButtons ? "small" : "default"} onClick={handleSortClick} isDisabled={!props.column.sortable} priority={sortPriority} aria-label={t("page.dataGrid.column.sortAriaLabel", {
        columnName: props.column.headerName
      })} />}{showMenuButton && columnMenu.showColumnMenu && <Dropdown isOpen={isMenuOpen} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)} placement="bottom-end" onButtonClick={handleMenuButtonClick} isFocusManagementDisabled={true} isKeyboardNavigationDisabled={true} dropdownControl={<IconButton icon="navigation/menu" size={useSmallButtons ? "small" : "default"} priority={menuPriority} aria-label={t("page.dataGrid.column.optionsAriaLabel", {
        columnName: props.column.headerName
      })} />}><ColumnHeaderMenu column={props.column} columnPinned={props.columnState.pinned} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} /></Dropdown>}</>;
    $[62] = columnHasMenuContent;
    $[63] = columnMenu.showColumnMenu;
    $[64] = handleSortClick;
    $[65] = isMenuOpen;
    $[66] = isMobile;
    $[67] = menuPriority;
    $[68] = props.column;
    $[69] = props.columnState.pinned;
    $[70] = showMenuButton;
    $[71] = showSortButton;
    $[72] = sortIconValue;
    $[73] = sortPriority;
    $[74] = t;
    $[75] = useSmallButtons;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== t21 || $[78] !== t22) {
    t23 = <div className={t21}>{t22}</div>;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== isPinned) {
    t24 = isPinned && <span style={PINNED_INDICATOR_STYLE} />;
    $[80] = isPinned;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[82] !== isBeingResized || $[83] !== props.columnState.pinned) {
    t25 = !isBeingResized && props.columnState.pinned === "LEFT" && <span style={PINNED_LEFT_EDGE_LINE_STYLE} />;
    $[82] = isBeingResized;
    $[83] = props.columnState.pinned;
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  let t26;
  if ($[85] !== isBeingResized || $[86] !== props.columnState.pinned) {
    t26 = !isBeingResized && props.columnState.pinned === "RIGHT" && <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />;
    $[85] = isBeingResized;
    $[86] = props.columnState.pinned;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== isBeingResized || $[89] !== isRightPinned) {
    t27 = isBeingResized && <span style={isRightPinned ? RESIZE_INDICATOR_LEFT_STYLE : RESIZE_INDICATOR_STYLE} />;
    $[88] = isBeingResized;
    $[89] = isRightPinned;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  let t28;
  if ($[91] !== isBeingResized || $[92] !== isPinned) {
    t28 = !isBeingResized && !isPinned && <span style={COLUMN_SEPARATOR_STYLE} />;
    $[91] = isBeingResized;
    $[92] = isPinned;
    $[93] = t28;
  } else {
    t28 = $[93];
  }
  let t29;
  if ($[94] !== columnResizingAllowed || $[95] !== handleResizePointerDown || $[96] !== isBeingResized || $[97] !== isRightPinned || $[98] !== props.column.resizable) {
    t29 = columnResizingAllowed && props.column.resizable !== false && !isBeingResized && <span className={isRightPinned ? "absolute -left-[2px] top-0 h-full w-[5px] cursor-col-resize z-(--z-content-loader) flex items-stretch justify-center" : "absolute -right-[2px] top-0 h-full w-[5px] cursor-col-resize z-(--z-content-loader) flex items-stretch justify-center"} onPointerDown={handleResizePointerDown}><span className="w-px h-full hover:bg-blue-400 pointer-events-none" /></span>;
    $[94] = columnResizingAllowed;
    $[95] = handleResizePointerDown;
    $[96] = isBeingResized;
    $[97] = isRightPinned;
    $[98] = props.column.resizable;
    $[99] = t29;
  } else {
    t29 = $[99];
  }
  let t30;
  if ($[100] !== isDropLeft) {
    t30 = isDropLeft && <span className="absolute left-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />;
    $[100] = isDropLeft;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== isDropRight) {
    t31 = isDropRight && <span className="absolute right-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />;
    $[102] = isDropRight;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] !== ariaSortValue || $[105] !== attributes || $[106] !== props.column.key || $[107] !== t13 || $[108] !== t14 || $[109] !== t15 || $[110] !== t20 || $[111] !== t23 || $[112] !== t24 || $[113] !== t25 || $[114] !== t26 || $[115] !== t27 || $[116] !== t28 || $[117] !== t29 || $[118] !== t30 || $[119] !== t31) {
    t32 = <th ref={t13} className={t14} scope="col" style={t15} {...attributes} role="columnheader" aria-sort={ariaSortValue} data-column={props.column.key}>{t20}{t23}{t24}{t25}{t26}{t27}{t28}{t29}{t30}{t31}</th>;
    $[104] = ariaSortValue;
    $[105] = attributes;
    $[106] = props.column.key;
    $[107] = t13;
    $[108] = t14;
    $[109] = t15;
    $[110] = t20;
    $[111] = t23;
    $[112] = t24;
    $[113] = t25;
    $[114] = t26;
    $[115] = t27;
    $[116] = t28;
    $[117] = t29;
    $[118] = t30;
    $[119] = t31;
    $[120] = t32;
  } else {
    t32 = $[120];
  }
  return t32;
}
function _temp10() {
  return "bg-white";
}
function _temp1() {
  return "bg-gray-50";
}
function _temp0() {
  return "bg-blue-50";
}
function _temp9(event_1) {
  event_1.stopPropagation();
}
function _temp8() {
  return "descending" as const;
}
function _temp7() {
  return "ascending" as const;
}
function _temp6() {}
function _temp5() {
  return "primary" as const;
}
function _temp4() {
  return "navigation/unfold_more" as const;
}
function _temp3() {
  return "navigation/arrow_downward" as const;
}
function _temp2() {
  return "navigation/arrow_upward" as const;
}
function _temp() {
  return false;
}